import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { LoyaltyMember } from '@/shared/api/loyalty'

export interface LoyaltyState {
	isLoading: boolean
	memberRecord: LoyaltyMember | null
	notFound: boolean
	lookupError: string | null
	enrollmentChecked: boolean
	memberCreated: boolean
}

const initialState: LoyaltyState = {
	isLoading: false,
	memberRecord: null,
	notFound: false,
	lookupError: null,
	enrollmentChecked: false,
	memberCreated: false,
}

const loyaltySlice = createSlice({
	name: 'loyalty',
	initialState,
	reducers: {
		setLoading(state, action: PayloadAction<boolean>) {
			state.isLoading = action.payload
		},
		setMember(state, action: PayloadAction<LoyaltyMember>) {
			state.memberRecord = action.payload
			state.notFound = false
			state.lookupError = null
		},
		setNotFound(state, action: PayloadAction<boolean>) {
			state.notFound = action.payload
			// A 404 response means the lookup succeeded — any prior network/5xx error is resolved.
			if (action.payload) state.lookupError = null
		},
		setLookupError(state, action: PayloadAction<string | null>) {
			state.lookupError = action.payload
		},
		// R3 — reset cascade: always dispatch alongside clearLoyaltyDiscount() + calculate()
		resetLoyalty(state) {
			state.memberRecord = null
			state.notFound = false
			state.lookupError = null
			state.enrollmentChecked = false
			state.memberCreated = false
			state.isLoading = false
		},
		setEnrollmentChecked(state, action: PayloadAction<boolean>) {
			state.enrollmentChecked = action.payload
		},
		setMemberCreated(state, action: PayloadAction<boolean>) {
			state.memberCreated = action.payload
		},
	},
})

export const {
	setLoading,
	setMember,
	setNotFound,
	setLookupError,
	resetLoyalty,
	setEnrollmentChecked,
	setMemberCreated,
} = loyaltySlice.actions

export default loyaltySlice.reducer

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectLoyaltyMember = (state: { loyalty: LoyaltyState }) => state.loyalty.memberRecord
export const selectLoyaltyNotFound = (state: { loyalty: LoyaltyState }) => state.loyalty.notFound
export const selectLoyaltyIsLoading = (state: { loyalty: LoyaltyState }) => state.loyalty.isLoading
export const selectLoyaltyLookupError = (state: { loyalty: LoyaltyState }) =>
	state.loyalty.lookupError
export const selectEnrollmentChecked = (state: { loyalty: LoyaltyState }) =>
	state.loyalty.enrollmentChecked
export const selectMemberCreated = (state: { loyalty: LoyaltyState }) => state.loyalty.memberCreated
